import { useEffect, useRef } from 'react'
import { Outlet, useParams, useNavigate } from 'react-router-dom'
import { useScenario } from '../hooks/useScenario'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '../components/LoadingSpinner'

/**
 * ScenarioLayout
 * 
 * Top-level layout for all scenario-related routes.
 * Responsibilities:
 * 1. Manages the Scenario lifecycle (subscription/unsubscription) ONCE for all child routes.
 * 2. Handles Loading/Error/AccessDenied states centrally.
 * 3. Prevents "thrashing" of SignalR connections when navigating between scenario sub-pages.
 */
export function ScenarioLayout() {
    const { user, authChecked, login } = useAuth()
    const loginTriggeredRef = useRef(false)

    const hasCachedAuth = (() => {
        try {
            return !!localStorage.getItem('auth_user')
        } catch {
            return false
        }
    })()

    useEffect(() => {
        if (!authChecked) return
        if (user) return
        if (hasCachedAuth) return // Session is being restored (offline-first)
        if (loginTriggeredRef.current) return
        loginTriggeredRef.current = true
        login()
    }, [authChecked, user, hasCachedAuth, login])

    if (!authChecked) {
        return (
            <div className="app-container">
                <LoadingSpinner message="Checking sign-in..." />
            </div>
        )
    }

    if (!user) {
        return (
            <div className="app-container">
                <LoadingSpinner message={hasCachedAuth ? 'Restoring session...' : 'Redirecting to sign in...'} />
            </div>
        )
    }

    return <ScenarioLayoutAuthenticated />
}

function ScenarioLayoutAuthenticated() {
    const { scenarioId } = useParams<{ scenarioId: string }>()
    const navigate = useNavigate()

    // This hook manages the subscription. Because this component remains mounted
    // while navigating between children (List/Dashboard/Flows), the connection persists.
    const { loading, error, accessDenied, deleted } = useScenario(scenarioId || '')

    if (loading) {
        return (
            <div className="app-container">
                <LoadingSpinner message="Syncing Scenario..." />
            </div>
        )
    }

    if (deleted) {
        return (
            <div className="app-container">
                <div className="error-container">
                    <h2>Scenario Deleted</h2>
                    <p>This scenario has been deleted.</p>
                    <button onClick={() => navigate('/', { replace: true })} className="btn primary">
                        Return Home
                    </button>
                </div>
            </div>
        )
    }

    if (accessDenied) {
        return (
            <div className="app-container">
                <div className="error-container">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view this scenario.</p>
                    <button onClick={() => navigate('/', { replace: true })} className="btn primary">
                        Return Home
                    </button>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="app-container">
                <div className="error-container">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="btn secondary">
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    // Render child route
    return <Outlet />
}
